import { useMemo, useState } from "react";
import { PageShell } from "../../components/service/PageShell";
import { ServiceHero, CtaPrimary, CtaGhost, FeatureGrid, StepsList, FaqBlock, Section, Overline, SectionTitle, Accent, Reveal } from "../../components/service/Blocks";
import { LeadForm } from "../../components/LeadForm";
import { SITE, waLink } from "../../data/site";
import { Minus, Plus, Rocket, GraduationCap, Link2, Repeat, AppWindow, MonitorSmartphone, Presentation, FolderOpen, Headset, BadgePercent, Wallet, Check, Trophy } from "lucide-react";

const WA_MSG = "Vim pelo site. Quero saber como ser licenciado iGreen.";

const CONNECTIONS = [
  { name: "Conexão Green", desc: "Desconto na conta de luz para residências e pequenos negócios, sem obra e sem investimento.", main: "Até 4% recorrente", net: "+ até 3,5% sobre a rede" },
  { name: "Conexão Livre", desc: "Mercado Livre de Energia para empresas de média e alta tensão.", main: "Até 2% recorrente", net: "+ até 1,5% sobre a rede" },
  { name: "Conexão Solar", desc: "Placas no telhado do cliente sem investimento inicial; no fim do contrato, o sistema é dele.", main: "2% recorrente", net: "+ 1,5% sobre a rede" },
  { name: "Conexão Placas", desc: "Venda do sistema solar completo, com projeto, instalação e suporte técnico da iGreen.", main: "10% por projeto", net: "+ 3,5% sobre a rede" },
  { name: "Conexão Telecom", desc: "Planos de celular 5G sem fidelidade, com internet que acumula.", main: "R$ 7 por plano, todo mês", net: "+ até R$ 10 sobre a rede" },
  { name: "Conexão Seguros", desc: "Seguro auto e moto com pagamento mensal e sem fidelidade.", main: "5% recorrente", net: "+ até 7% sobre a rede" },
  { name: "Conexão Mob", desc: "Eletropostos de 7 kW a 80 kW e aplicativo de recarga para carros elétricos.", main: "Até 3% recorrente", net: "no eletroposto e nas recargas" },
  { name: "Conexão Expansão", desc: "Indique novos licenciados e forme a sua equipe comercial.", main: "Até R$ 300 por licença", net: "+ até R$ 500 sobre a rede" },
];

const SIM_TYPES = [
  { key: "casa", label: "Casa", avg: 350, def: 25 },
  { key: "padaria", label: "Padaria", avg: 4500, def: 3 },
  { key: "bar", label: "Bar ou lanchonete", avg: 3000, def: 2 },
  { key: "escola", label: "Escola", avg: 10000, def: 1 },
  { key: "condominio", label: "Condomínio", avg: 20000, def: 1 },
  { key: "industria", label: "Indústria", avg: 20000, def: 1 },
  { key: "supermercado", label: "Supermercado", avg: 30000, def: 1 },
];

const CAREER = [
  { n: "1", name: "Sênior", req: "5 conexões diretas · 10 mil pontos", roy: "até R$ 70 royalties Expansão" },
  { n: "2", name: "Gestor", req: "10 conexões diretas · 50 mil pontos", roy: "até R$ 130 royalties Expansão" },
  { n: "3", name: "Executivo", req: "20 conexões diretas · 150 mil pontos", roy: "até R$ 190 royalties Expansão" },
  { n: "4", name: "Diretor", req: "40 conexões diretas · 500 mil pontos · 10 licenciados diretos", roy: "até R$ 250 royalties Expansão" },
  { n: "5", name: "Acionista", req: "80 conexões diretas · 1 milhão de pontos · 15 licenciados", roy: "até R$ 300 royalties Expansão" },
  { n: "6", name: "Royal e Embaixador", req: "o topo do plano de carreira", roy: "royalties Green de até 2,6% sobre a equipe" },
];

const FAQ = [
  { q: "Preciso ser licenciado para ganhar indicando?", a: "Não. Para ganhar cashback indicando amigos, você só precisa ser cliente iGreen: cadastre a sua conta de luz pelo link de autoconexão (é grátis) e indique pelo app iGreen Club. A licença é outra coisa: é para quem quer atender clientes como um negócio, com comissão recorrente, e ela é paga." },
  { q: "Isso é pirâmide?", a: "Não. A iGreen presta serviços reais, como energia, telefonia, seguro e recarga elétrica, a clientes reais, e você ganha sobre o que os seus clientes pagam. Indicar outros licenciados é opcional: dá para construir a renda só com a sua própria carteira." },
  { q: "Quanto eu posso ganhar?", a: "Depende das conexões que você faz e de os clientes seguirem ativos. Não existe renda garantida. O simulador mostra uma projeção com os percentuais oficiais, e na conversa eu te apresento o plano de negócios completo." },
  { q: "Preciso recrutar pessoas para crescer?", a: "Não. Até a qualificação de Executivo, você pode subir só com os pontos da sua própria carteira. Formar equipe acelera os ganhos e passa a ser exigido a partir de Diretor." },
  { q: "Preciso ter experiência em vendas?", a: "Não. A iGreen Academy ensina do zero, com treinamentos ao vivo toda semana e material oficial, e eu acompanho você nos primeiros atendimentos." },
  { q: "Dá para conciliar com o meu trabalho atual?", a: "Sim. Muitos licenciados começam no tempo livre, atendendo família, amigos e empresas que já conhecem. É você quem define o ritmo." },
  { q: "Preciso de CNPJ ou de um ponto comercial?", a: "Não é preciso ponto comercial nem estoque, e você pode começar como pessoa física. Para receber os repasses, a iGreen orienta sobre a emissão de nota fiscal, e eu explico esse passo com você." },
  { q: "Como funciona a renovação?", a: "A licença vale 365 dias. A renovação tem o mesmo valor da licença, pode ser corrigida pelo IPCA e tem 30 dias de carência para manter os bônus da rede." },
  { q: "Quais são as regras para atuar?", a: "O licenciado é independente: não é funcionário da iGreen, se identifica com nome e ID, usa apenas material oficial e não faz promessas ao cliente. Isso protege você, o cliente e a marca." },
];

const fmt = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

const RecurrenceSimulator = () => {
  const [qty, setQty] = useState(Object.fromEntries(SIM_TYPES.map((t) => [t.key, t.def])));
  const total = useMemo(() => SIM_TYPES.reduce((acc, t) => acc + qty[t.key] * t.avg, 0), [qty]);
  const commission = total * 0.04;
  const bump = (k, d) => setQty((q) => ({ ...q, [k]: Math.max(0, q[k] + d) }));

  return (
    <div className="rounded-2xl border border-[#00E676]/20 bg-[#0B261E]/70 backdrop-blur-md p-7 sm:p-9" data-testid="licenciado-simulator">
      <p className="text-sm text-emerald-100/60 mb-6">É você quem define o tamanho da sua carteira.</p>
      <div className="space-y-3">
        {SIM_TYPES.map((t) => (
          <div key={t.key} className="flex items-center justify-between gap-3 rounded-xl bg-[#04120E]/70 border border-white/10 px-4 py-3" data-testid={`sim-row-${t.key}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-white truncate">{t.label}</p>
              <p className="text-[11px] text-emerald-100/40">conta média de {fmt(t.avg)}</p>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              <button onClick={() => bump(t.key, -1)} data-testid={`sim-minus-${t.key}`} aria-label="menos" className="grid place-items-center w-8 h-8 rounded-full border border-white/15 text-emerald-100/70 hover:border-[#00E676]/50 hover:text-[#00E676] transition-colors"><Minus size={14} /></button>
              <span className="w-7 text-center font-heading font-bold text-white">{qty[t.key]}</span>
              <button onClick={() => bump(t.key, 1)} data-testid={`sim-plus-${t.key}`} aria-label="mais" className="grid place-items-center w-8 h-8 rounded-full border border-white/15 text-emerald-100/70 hover:border-[#00E676]/50 hover:text-[#00E676] transition-colors"><Plus size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-7 border-t border-white/10 pt-6">
        <p className="text-sm text-emerald-100/70">
          Total das contas: <strong className="text-white">{fmt(total)}</strong> × até 4%
        </p>
        <p className="font-heading text-4xl sm:text-5xl font-extrabold text-[#00E676] mt-2" data-testid="sim-monthly-commission">
          {fmt(commission)} <span className="text-base font-medium text-emerald-100/50">por mês</span>
        </p>
        <p className="text-xs text-emerald-100/50 mt-1.5">recorrente, enquanto os clientes seguirem ativos</p>
        <p className="mt-3 text-sm text-emerald-100/70">Em 12 meses: <strong className="text-white">{fmt(commission * 12)}</strong></p>
      </div>
      <p className="mt-5 text-[11px] text-emerald-100/40 leading-relaxed">
        Simulação ilustrativa com a comissão recorrente direta de até 4% da Conexão Green sobre a fatura de energia e
        contas médias por tipo de cliente. Não é promessa nem garantia de ganho: o resultado depende das conexões que você faz e de os clientes seguirem ativos.
      </p>
    </div>
  );
};

const LicenseCard = ({ name, desc, price, installments, renewal, features, highlight, msg, testid, note }) => (
  <div
    data-testid={testid}
    className={`relative h-full rounded-2xl border p-8 flex flex-col transition-transform duration-300 hover:-translate-y-1.5 ${
      highlight ? "border-[#00E676]/50 bg-[#0B261E]/85 shadow-[0_0_36px_rgba(0,230,118,0.15)]" : "border-white/10 bg-[#0B261E]/50"
    }`}
  >
    {highlight && <span className="absolute -top-3 left-8 rounded-full bg-[#00E676] text-[#04120E] text-[10px] font-bold px-3 py-1">MAIS COMPLETA</span>}
    <h3 className="font-heading text-2xl font-extrabold text-white">{name}</h3>
    <p className="mt-1.5 text-xs text-emerald-100/50">{desc}</p>
    <p className="mt-6 font-heading text-4xl font-extrabold text-[#00E676]">{price}</p>
    <p className="mt-1 text-xs text-emerald-100/45">{installments} · Renovação anual de {renewal}</p>
    <ul className="mt-6 space-y-2.5 text-xs text-emerald-100/65 leading-relaxed flex-1">
      {features.map((f) => (
        <li key={f} className="flex gap-2 items-start"><Check size={13} className="text-[#00E676] mt-0.5 shrink-0" />{f}</li>
      ))}
    </ul>
    {note && <p className="mt-4 text-[11px] text-[#A3E635]/80">{note}</p>}
    <a
      href={waLink(msg)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`${testid}-cta`}
      className="mt-7 inline-flex justify-center rounded-full bg-[#00E676] hover:bg-[#00C853] text-[#04120E] text-sm font-bold px-6 py-3.5 transition-colors duration-300"
    >
      Escolher {name}
    </a>
  </div>
);

export default function SejaLicenciado() {
  return (
    <PageShell testid="page-licenciado">
      <ServiceHero
        testid="licenciado-hero"
        overline="Plano de negócios iGreen · empreenda com o ecossistema"
        title={["Transforme indicações em", "renda recorrente."]}
        sub="Como licenciado iGreen Energy, você leva economia em energia, telefonia e seguros a residências e empresas, e recebe comissão todos os meses enquanto os seus clientes seguem ativos. Sem estoque, sem ponto comercial e com treinamento oficial desde o primeiro dia."
        bullets={["+800 mil clientes no ecossistema", "Comissão recorrente", "Treinamento completo", "App para acompanhar tudo"]}
        primary={<CtaPrimary href={SITE.licenciadoUrl} testid="licenciado-join-cta">Quero ser licenciado</CtaPrimary>}
        secondary={<CtaGhost href={waLink(WA_MSG)} testid="licenciado-doubt-cta">Tirar dúvidas no WhatsApp</CtaGhost>}
      />

      <Section className="bg-[#061A14]" testid="licenciado-numeros">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            ["+800 mil", "clientes no ecossistema"],
            ["+R$ 4,2 mi", "em recorrência já distribuída aos licenciados"],
            ["+R$ 1 bi", "em boletos de energia pagos"],
            ["3.000 m²", "de sede própria em Uberlândia (MG)"],
          ].map(([v, l], i) => (
            <Reveal key={l} delay={i * 0.08}>
              <div className="rounded-2xl border border-[#00E676]/20 bg-[#0B261E]/70 p-6 text-center">
                <p className="font-heading text-2xl sm:text-3xl font-extrabold text-[#00E676]">{v}</p>
                <p className="mt-2 text-xs text-emerald-100/55 leading-snug">{l}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-emerald-100/40">Números divulgados pela iGreen Energy no plano de negócios oficial.</p>
        <Reveal delay={0.1}>
          <div className="mt-12 rounded-2xl border border-white/10 bg-[#0B261E]/50 p-8">
            <h3 className="font-heading text-lg font-bold text-white mb-6">Uma empresa em crescimento acelerado</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {[
                ["2021", "Nasce a iGreen", "Fundada em Minas Gerais com o propósito de enriquecer vidas com a sustentabilidade."],
                ["2023", "Gigantes do setor investem", "Grupos do setor de energia adquirem 50% da iGreen e investem bilhões."],
                ["2024", "Chega a Telecom", "Mais de 100 mil clientes em dois anos e o início da iGreen Telecom."],
                ["2025", "A maior do setor", "Mais de 450 mil clientes conectados."],
                ["2026", "Expansão internacional", "Mais de 800 mil clientes, a Conexão Seguros e a iGreen Mobile, nos Estados Unidos."],
              ].map(([y, t, d]) => (
                <div key={y} className="border-l-2 border-[#00E676]/40 pl-4">
                  <p className="font-heading text-xl font-extrabold text-[#00E676]">{y}</p>
                  <p className="mt-1 text-xs font-bold text-white">{t}</p>
                  <p className="mt-1 text-[11px] text-emerald-100/50 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </Section>

      <Section testid="licenciado-o-que-faz">
        <Reveal>
          <Overline>Como funciona</Overline>
          <SectionTitle>Afinal, o que faz um <Accent>licenciado iGreen</Accent>?</SectionTitle>
        </Reveal>
        <div className="mt-12">
          <FeatureGrid
            testid="licenciado-what-grid"
            items={[
              { icon: Rocket, title: "Ative a sua licença", text: "Você passa a representar as soluções do ecossistema iGreen como licenciado independente: sem estoque, sem ponto comercial e no seu ritmo." },
              { icon: GraduationCap, title: "Aprenda com a iGreen Academy", text: "Treinamentos oficiais toda semana, para cada conexão, e o app iGreen Connect para cadastrar clientes e acompanhar resultados." },
              { icon: Link2, title: "Conecte clientes", text: "Leve economia a residências e empresas com energia, telefonia, seguro e mobilidade elétrica, tudo pelo aplicativo." },
              { icon: Repeat, title: "Construa a sua recorrência", text: "Cada cliente ativo gera comissão todos os meses. A carteira se acumula e a sua renda não recomeça do zero a cada mês." },
            ]}
          />
        </div>
      </Section>

      <Section className="bg-[#061A14]" testid="licenciado-conexoes">
        <Reveal>
          <Overline>Um ecossistema, várias fontes</Overline>
          <SectionTitle className="max-w-3xl">Você oferece o que <Accent>todo mundo já paga</Accent>.</SectionTitle>
          <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55">
            Conta de luz, celular e seguro são despesas de todo mês. Cada cliente que você conecta pode gerar comissão enquanto continuar ativo.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {CONNECTIONS.map((c, i) => (
            <Reveal key={c.name} delay={(i % 4) * 0.06}>
              <div className="h-full rounded-2xl border border-white/10 bg-[#0B261E]/60 p-6 transition-[border-color,transform] duration-300 hover:border-[#00E676]/45 hover:-translate-y-1" data-testid={`conexao-${c.name.toLowerCase().replace(" ", "-")}`}>
                <p className="font-heading font-bold text-white text-sm">{c.name}</p>
                <p className="mt-2 text-xs text-emerald-100/55 leading-relaxed">{c.desc}</p>
                <p className="mt-4 text-sm font-heading font-bold text-[#00E676]">{c.main}</p>
                <p className="text-[11px] text-emerald-100/45">{c.net}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-emerald-100/40 leading-relaxed max-w-3xl">
          Comissões do plano de negócios oficial da iGreen (outubro/2026). Os ganhos sobre a rede somam geração indireta,
          royalties e equiparação, conforme a qualificação e o status PRO, e há bônus extras nas campanhas vigentes.
        </p>
      </Section>

      <Section testid="licenciado-simulador">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Overline>Simulador de recorrência</Overline>
            <SectionTitle>Quanto você poderia receber <Accent>todo mês</Accent>?</SectionTitle>
            <p className="mt-4 text-sm sm:text-base text-emerald-100/55 leading-relaxed">
              Monte uma carteira de exemplo com casas e empresas conectadas à Conexão Green. O simulador soma as contas
              de luz e aplica a comissão recorrente de até 4%. Altere as quantidades e veja o resultado.
            </p>
            <div className="mt-8">
              <CtaPrimary href={SITE.licenciadoUrl} testid="licenciado-build-cta">Quero construir a minha carteira</CtaPrimary>
            </div>
          </Reveal>
          <Reveal delay={0.12}><RecurrenceSimulator /></Reveal>
        </div>
      </Section>

      <Section className="bg-[#061A14]" testid="licenciado-estrutura">
        <Reveal>
          <Overline>Você não começa sozinho</Overline>
          <SectionTitle className="max-w-3xl">A estrutura que vem <Accent>com a sua licença</Accent>.</SectionTitle>
          <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55">
            Tecnologia, treinamento e suporte de uma empresa com mais de 800 mil clientes, para você focar em atender bem.
          </p>
        </Reveal>
        <div className="mt-12">
          <FeatureGrid
            testid="licenciado-structure-grid"
            items={[
              { icon: AppWindow, title: "App iGreen Connect", text: "Cadastre clientes, acompanhe a sua recorrência e a sua rede e solicite saques pelo celular." },
              { icon: MonitorSmartphone, title: "Escritório Virtual", text: "Painel completo para acompanhar clientes, equipe, comissões e treinamentos." },
              { icon: Presentation, title: "Treinamentos e eventos", text: "Aulas ao vivo toda semana para cada conexão, com gravação, além da Live Connect e do Grand Show todo mês." },
              { icon: FolderOpen, title: "Materiais de apoio", text: "Apresentações, vídeos e peças oficiais para divulgar com segurança e dentro das regras da marca." },
              { icon: Headset, title: "Suporte ao licenciado", text: "Atendimento oficial da iGreen e o meu acompanhamento direto, do primeiro cliente em diante." },
              { icon: BadgePercent, title: "iGreen Club", text: "Clube de descontos com benefícios exclusivos para você e para os seus clientes." },
              { icon: Wallet, title: "iGreen Pay", text: "Conta digital no plano Basic, gratuito, para receber e movimentar os seus ganhos." },
            ]}
          />
        </div>
      </Section>

      <Section testid="licenciado-carreira">
        <Reveal>
          <Overline>Plano de carreira</Overline>
          <SectionTitle className="max-w-3xl">Cada nível <Accent>aumenta os seus ganhos</Accent>.</SectionTitle>
          <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55">
            As qualificações reconhecem o seu crescimento. A cada nível, os royalties sobre a sua equipe aumentam. Até Executivo, você pode subir só com os pontos da sua própria carteira, sem precisar recrutar.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAREER.map((c, i) => (
            <Reveal key={c.name} delay={(i % 3) * 0.08}>
              <div className="rounded-2xl border border-white/10 bg-[#0B261E]/60 p-6 h-full transition-[border-color] duration-300 hover:border-[#00E676]/40" data-testid={`career-${c.n}`}>
                <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-[#00E676]/12 border border-[#00E676]/25 text-[#00E676] font-heading font-extrabold text-sm">
                  <Trophy size={15} />
                </span>
                <p className="mt-4 font-heading text-lg font-bold text-white">{c.name}</p>
                <p className="mt-1 text-xs text-emerald-100/50">{c.req}</p>
                <p className="mt-3 text-xs font-semibold text-[#00E676]">{c.roy}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-[11px] text-emerald-100/40 leading-relaxed max-w-3xl">
          Requisitos das Regras de Qualificações oficiais da iGreen (outubro/2026). Pontos: na Conexão Green contam os kWh contratados; na Telecom, 200 pontos por portabilidade ativa.
        </p>
      </Section>

      <Section className="bg-[#061A14]" testid="licenciado-licencas">
        <div id="licencas">
          <Reveal>
            <Overline>Planos · licenças</Overline>
            <SectionTitle>O futuro é de quem <Accent>empreende</Accent>.</SectionTitle>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55 leading-relaxed">
              Licença anual e renovável. Comece pela Plus ou tenha todas as conexões com a Full.
            </p>
            <p className="mt-4 max-w-2xl text-xs text-emerald-100/45 leading-relaxed">
              <strong className="text-white">A licença é paga</strong> (valores abaixo) e é para quem quer atender clientes
              como um negócio. Se você só quer <strong className="text-white">indicar amigos e ganhar cashback</strong>, não precisa dela: cadastre-se como cliente grátis.
            </p>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl">
            <Reveal>
              <LicenseCard
                testid="license-plus"
                name="Connect Plus"
                desc="Para começar pela telefonia, pelos seguros e pela expansão."
                price="R$ 997 à vista"
                installments="ou 12x de R$ 97"
                renewal="R$ 997"
                features={[
                  "App iGreen Connect com as conexões Telecom, Seguros e Expansão",
                  "Escritório Virtual",
                  "Treinamentos e eventos oficiais",
                  "Suporte ao licenciado",
                  "Materiais de apoio comerciais",
                  "iGreen Club e iGreen Pay (plano Basic)",
                ]}
                note="Upgrade para a Full quando quiser, por R$ 1.649"
                msg={`${WA_MSG}\nQuero a licença Connect Plus.`}
              />
            </Reveal>
            <Reveal delay={0.1}>
              <LicenseCard
                testid="license-full"
                name="Connect Full"
                desc="Uma licença com todas as conexões do ecossistema."
                price="R$ 1.997 à vista"
                installments="ou 12x de R$ 197,41"
                renewal="R$ 1.997"
                features={[
                  "App iGreen Connect com todas as conexões: Green, Livre, Solar, Placas, Telecom, Seguros e Expansão",
                  "Escritório Virtual",
                  "Treinamentos e eventos oficiais",
                  "Suporte completo ao licenciado",
                  "Suporte Telecom 24 h para os seus clientes",
                  "Materiais de apoio comerciais",
                  "iGreen Club e iGreen Pay (plano Basic)",
                ]}
                highlight
                msg={`${WA_MSG}\nQuero a licença Connect Full.`}
              />
            </Reveal>
          </div>
          <p className="mt-6 text-[11px] text-emerald-100/40 max-w-3xl leading-relaxed">
            Valores e itens das páginas oficiais das licenças Connect Plus e Full (iGreen Academy). A renovação pode ser corrigida pelo IPCA; confirmo o valor vigente antes do cadastro.
          </p>
        </div>
      </Section>

      <Section testid="licenciado-como-comecar">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Overline>Como começar</Overline>
            <SectionTitle>Do primeiro papo ao <Accent>primeiro cliente</Accent></SectionTitle>
          </Reveal>
          <StepsList
            testid="licenciado-steps"
            steps={[
              { n: "01", title: "A gente conversa", text: "Eu te mostro o plano de negócios oficial, os produtos e as regras, e tiro as suas dúvidas." },
              { n: "02", title: "Você escolhe a licença", text: "Plus ou Full, conforme os produtos que você quer oferecer." },
              { n: "03", title: "Cadastro e treinamento", text: "Você faz o cadastro e entra na trilha de primeiros passos da iGreen Academy." },
              { n: "04", title: "Você começa a conectar", text: "Com o app iGreen Connect você cadastra clientes e acompanha a sua recorrência, com o meu apoio." },
            ]}
          />
        </div>
      </Section>

      <FaqBlock testid="licenciado-faq" title={<>Antes de <Accent>decidir</Accent></>} items={FAQ} />

      <LeadForm
        overline="Vamos conversar?"
        title={<>O próximo a subir no palco <Accent>pode ser você</Accent>.</>}
        subtitle="Me conta o seu momento e eu te mostro o plano de negócios oficial, sem compromisso."
        defaultInterest="Quero ser licenciado"
      />
    </PageShell>
  );
}
